import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  RemoveNoticeFromListMutation,
  RemoveNoticeFromListMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { BasicSignal, getSignalTypes } from "lib/types/models";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateCachedNotices } from "../utils";

const removeNoticeFromListMutation = graphql(`
  mutation removeNoticeFromList($input: NoticeListEntryInput!) {
    removeNoticeFromList(input: $input) {
      id
      name
    }
  }
`);

/** Where the notice was removed from; the list itself comes from the response. */
type TrackingContext = {
  contextSource: string;
  signals: BasicSignal[];
  recordStage: string;
};

export function useRemoveNoticeFromList(
  { contextSource, signals, recordStage }: TrackingContext,
  options?: UseMutationOptions<
    RemoveNoticeFromListMutation,
    unknown,
    RemoveNoticeFromListMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();
  return useGraphQlMutation(removeNoticeFromListMutation, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      await queryClient.invalidateQueries(["noticeLists"]);
      const list = data.removeNoticeFromList;
      const signalTypes = getSignalTypes(signals);

      logEvent(EventNames.recordRemovedFromList, {
        "List name": list.name,
        "List id": list.id,
        "Data type": EventDataTypes.recordList,
        "Context source": contextSource,
        Stage: recordStage,
        "Signal types": signalTypes ? Object.keys(signalTypes) : [],
        Signals: signalTypes,
      });

      updateCachedNotices({
        queryClient,
        lookup: { type: "guid", guid: vars.input.noticeGuid },
        updateNoticesRow: (r) => ({
          ...r,
          lists: r.lists.filter((l) => l.id !== list.id),
        }),
      });

      options?.onSuccess?.(data, vars, ctx);
    },
  });
}
